<script lang="ts" setup>
import { entraAuthService } from '@/services/auth/entra-auth-service';
import type { SessionUser } from 'cesg-shared/dist/models';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import LiftPlan, { type LiftPlanProps } from '../LiftPlan.vue';
import type { SaveLiftPlanData } from '../logic/useLiftPlanLogic';
import LiftPlanSkeleton from './LiftPlanSkeleton.vue';
import { useLiftPlanMutation } from './useLiftPlanMutation.ts';
import { useLiftPlanQuery } from './useLiftPlanQuery.ts';
const route = useRoute();

const projectId = route.params.id as string;

const { data, isLoading } = useLiftPlanQuery(projectId);

const { mutate, isPending } = useLiftPlanMutation(projectId);
const liftPlanProps = computed((): LiftPlanProps => {
  const q = data.value;
  return {
    initialLiftPlan: {
      id: q?.id ?? '',
      name: q?.name ?? '',
      contractSum: q?.contractSum ?? 0,
      retentionPercentage: q?.retentionPercentage ?? 5,
      subtotalRows: (q?.subtotals || []).map((s) => ({
        ...s,
        label: s.description,
      })),
      entries: (q?.entries ?? []).map((e) => ({
        ...e,
        description: e.description ?? undefined,
        dueDate: e.dueDate ? new Date(e.dueDate) : undefined,
        invoicedDate: e.invoicedDate ? new Date(e.invoicedDate) : undefined,
        paymentDate: e.paymentDate ? new Date(e.paymentDate) : undefined,
        comment: e.comment ?? undefined,
      })),
    },
    user: entraAuthService.me.value as SessionUser,
    projectId,
  };
});

function onSave(body: SaveLiftPlanData) {
  mutate({
    body: {
      retentionPercentage: body.retentionPercentage,
      entries: body.entries.map(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ id: _id, dueDate, invoicedDate, paymentDate, ...rest }) => ({
          ...rest,
          dueDate: dueDate ? dueDate.toISOString().slice(0, 10) : null,
          invoicedDate: invoicedDate
            ? invoicedDate.toISOString().slice(0, 10)
            : null,
          paymentDate: paymentDate
            ? paymentDate.toISOString().slice(0, 10)
            : null,
        }),
      ),
      subtotals: body.subtotalRows.map(({ label, amount }) => ({
        description: label,
        amount,
      })),
    },
  });
}
</script>

<template>
  <LiftPlanSkeleton v-if="isLoading" />
  <LiftPlan
    v-else
    :key="data?.id"
    v-bind="liftPlanProps"
    :is-saving="isPending"
    @save="onSave"
  />
</template>
